/* eslint-disable max-lines */
import { get_token } from "@/router";
import { get_active_organization_id } from "@/utils/organization_profile";

let realtime_socket = null;
let portal_realtime_socket = null;
let portal_realtime_portal_id = null;
let portal_realtime_company_id = null;
let portal_reconnect_timer = null;
let portal_reconnect_attempts = 0;
let portal_manual_disconnect = false;
let reconnect_timer = null;
let reconnect_delay_ms = 1000;
let reconnect_attempts = 0;
let manual_disconnect = false;
let auth_checker = () => true;
const MAX_RECONNECT_DELAY_MS = 30000;
const SOCKET_CONNECTED_EVENT = "realtime:connected";
const SOCKET_DISCONNECTED_EVENT = "realtime:disconnected";

function get_socket_url() {
  const env_url = (process.env.VUE_APP_REALTIME_WS_URL || "").trim();
  if (env_url) return env_url;

  return `${get_window_socket_origin()}/ws/notifications/`;
}

function normalize_socket_path(path) {
  const normalized_path = String(path || "").trim().replace(/^\/?/, "/");
  return normalized_path.replace(/\/$/, "");
}

function get_socket_path(path_or_url) {
  const value = String(path_or_url || "").trim();
  if (!value) return "";

  try {
    return normalize_socket_path(new URL(value).pathname);
  } catch {
    return normalize_socket_path(value);
  }
}

function can_parse_socket_url(value) {
  return typeof URL.canParse === "function" && URL.canParse(value);
}

function get_parsed_socket_origin(value) {
  if (!can_parse_socket_url(value)) return "";
  return new URL(value).origin;
}

function get_raw_socket_origin(value) {
  return value.match(/^wss?:\/\//) ? value.replace(/\/$/, "") : "";
}

function get_socket_origin(origin_or_url) {
  const value = String(origin_or_url || "").trim();
  if (!value) return "";

  return get_parsed_socket_origin(value) || get_raw_socket_origin(value);
}

function get_portal_configured_realtime_path(company_id = null) {
  if (company_id) return process.env.VUE_APP_ORGANISATION_REALTIME_WS_PATH;
  return process.env.VUE_APP_PORTAL_REALTIME_WS_PATH || process.env.VUE_APP_PORTAL_REALTIME_WS_BASE_URL;
}

function get_default_portal_realtime_path(company_id = null) {
  if (company_id) return "/ws/organisation";
  return "/ws/portals";
}

function get_portal_realtime_path(company_id = null) {
  const configured_path = get_portal_configured_realtime_path(company_id);
  const default_path = get_default_portal_realtime_path(company_id);

  return get_socket_path(configured_path || default_path);
}

function build_portal_socket_path(portal_id, company_id = null) {
  const encoded_portal_id = encodeURIComponent(portal_id);
  const encoded_company_id = company_id ? encodeURIComponent(company_id) : null;
  const realtime_path = get_portal_realtime_path(encoded_company_id);

  return encoded_company_id
    ? `${realtime_path}/${encoded_company_id}/`
    : `${realtime_path}/${encoded_portal_id}/`;
}

function get_env_value(key) {
  return String(Reflect.get(process.env, key) ?? "").trim();
}

function get_portal_socket_path_base_key(company_id = null) {
  if (company_id) return "VUE_APP_ORGANISATION_REALTIME_WS_PATH";
  return "VUE_APP_PORTAL_REALTIME_WS_PATH";
}

function get_portal_socket_path_base(company_id = null) {
  return get_env_value(get_portal_socket_path_base_key(company_id));
}

function get_portal_socket_origin(company_id = null) {
  const origins = [
    get_portal_socket_path_base(company_id),
    get_env_value("VUE_APP_PORTAL_REALTIME_WS_BASE_URL"),
    get_socket_url(),
  ];

  return origins.map(get_socket_origin).find(Boolean) || "";
}

function get_window_socket_protocol() {
  return window.location.protocol === "https:" ? "wss:" : "ws:";
}

function get_window_socket_origin() {
  return `${get_window_socket_protocol()}//${window.location.host}`;
}

function build_portal_socket_base_url(origin, portal_path) {
  const socket_origin = origin || get_window_socket_origin();
  return `${socket_origin}${portal_path}`;
}

function get_portal_socket_base_url(portal_id, company_id = null) {
  const portal_path = build_portal_socket_path(portal_id, company_id);
  const origin = get_portal_socket_origin(company_id);

  return build_portal_socket_base_url(origin, portal_path);
}

function build_socket_urls(base_url, token) {
  const separator = base_url.includes("?") ? "&" : "?";
  const safe_socket_url = `${base_url}${separator}token=***`;
  const socket_url = `${base_url}${separator}token=${encodeURIComponent(token)}`;
  return {
    socket_url,
    safe_socket_url,
  };
}

function clear_reconnect_timer() {
  if (!reconnect_timer) return;
  clearTimeout(reconnect_timer);
  reconnect_timer = null;
}

function can_connect() {
  const url = get_socket_url();
  return Boolean(url) && auth_checker();
}

function socket_is_active(socket) {
  if (!socket) return false;
  return socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING;
}

function should_reuse_existing_socket(force_reconnect, existing_socket) {
  if (force_reconnect) return false;
  return socket_is_active(existing_socket);
}

function get_expected_portal_socket_id(portal_id, company_id = null) {
  return company_id ? portal_realtime_portal_id : portal_id;
}

function is_same_portal_socket_target(portal_id, company_id = null) {
  const expected_portal_id = get_expected_portal_socket_id(portal_id, company_id);
  return portal_realtime_company_id === company_id && portal_realtime_portal_id === expected_portal_id;
}

function should_reuse_portal_socket(force_reconnect, portal_id, company_id = null) {
  if (force_reconnect) return false;
  return is_same_portal_socket_target(portal_id, company_id) && socket_is_active(portal_realtime_socket);
}

function can_open_portal_socket(portal_id) {
  return Boolean(portal_id && auth_checker());
}

function get_portal_socket_options(options = {}) {
  return {
    force: options.force ?? false,
    delay_ms: options.delay_ms ?? reconnect_delay_ms,
    is_reconnect: options.is_reconnect === true,
  };
}

function close_existing_portal_socket() {
  if (!portal_realtime_socket) return;
  portal_manual_disconnect = true;
  portal_realtime_socket.close();
}

function clear_portal_reconnect_timer() {
  if (!portal_reconnect_timer) return;
  clearTimeout(portal_reconnect_timer);
  portal_reconnect_timer = null;
}

function dispatch_socket_event(event_name, detail = {}) {
  window.dispatchEvent(
    new CustomEvent(event_name, {
      detail,
    })
  );
}

function emit_realtime_event(payload) {
  window.dispatchEvent(
    new CustomEvent("realtime:event", {
      detail: payload,
    })
  );

  // Report/portal-management realtime channel (keeps report handlers separate from event-management handlers)
  if (payload?.module === "portal_management" || payload?.module === "portal_reports") {
    window.dispatchEvent(
      new CustomEvent("realtime:report", {
        detail: payload,
      })
    );
  }

  if (payload?.module === "document_approvals") {
    window.dispatchEvent(
      new CustomEvent("realtime:document-approvals", {
        detail: payload,
      })
    );
  }

  if (payload?.module === "portal_boards") {
    window.dispatchEvent(
      new CustomEvent("realtime:portal-boards", {
        detail: payload,
      })
    );
  }
}

function get_next_reconnect_delay(base_delay_ms) {
  const safe_base_delay = Math.max(1000, Number(base_delay_ms) || 1000);
  const delay = safe_base_delay * (2 ** reconnect_attempts);
  return Math.min(delay, MAX_RECONNECT_DELAY_MS);
}

function get_next_portal_reconnect_delay(base_delay_ms) {
  const safe_base_delay = Math.max(1000, Number(base_delay_ms) || 1000);
  const delay = safe_base_delay * (2 ** portal_reconnect_attempts);
  return Math.min(delay, MAX_RECONNECT_DELAY_MS);
}

function register_socket_handlers(socket, socket_url, safe_socket_url, reconnect_delay_ms) {
  socket.onopen = () => {
    clear_reconnect_timer();
    reconnect_attempts = 0;
    dispatch_socket_event(SOCKET_CONNECTED_EVENT, {
      socket_url: safe_socket_url,
      reconnected: socket.__was_reconnect === true,
    });
  };

  socket.onmessage = (event) => {
    let payload;
    try {
      payload = JSON.parse(event.data);
    } catch (error) {
      console.error("[realtime] Invalid WebSocket message:", event.data);
      return;
    }
    emit_realtime_event(payload);
  };

  socket.onerror = (error) => {
    console.error("[realtime] WebSocket error:", error);
  };

  socket.onclose = () => {
    clear_reconnect_timer();
    realtime_socket = null;

    dispatch_socket_event(SOCKET_DISCONNECTED_EVENT, {
      socket_url: safe_socket_url,
      manual: manual_disconnect,
    });

    if (manual_disconnect || !can_connect()) {
      manual_disconnect = false;
      reconnect_attempts = 0;
      return;
    }

    const next_delay_ms = get_next_reconnect_delay(reconnect_delay_ms);
    reconnect_attempts += 1;
    reconnect_timer = setTimeout(() => {
      connect_realtime_socket({ delay_ms: reconnect_delay_ms, is_reconnect: true });
    }, next_delay_ms);
  };
}

function register_portal_socket_handlers(socket, safe_socket_url, portal_id, delay_ms) {
  socket.onopen = () => {
    clear_portal_reconnect_timer();
    portal_reconnect_attempts = 0;
    dispatch_socket_event("portal-realtime:connected", {
      socket_url: safe_socket_url,
      portal_id,
      reconnected: socket.__was_reconnect === true,
    });
  };

  socket.onmessage = (event) => {
    let payload;
    try {
      payload = JSON.parse(event.data);
    } catch (error) {
      console.error("[portal-realtime] Invalid WebSocket message:", event.data);
      return;
    }

    emit_realtime_event(payload);
  };

  socket.onerror = (error) => {
    console.error("[portal-realtime] WebSocket error:", error);
  };

  socket.onclose = () => {
    handle_portal_socket_close(safe_socket_url, portal_id, delay_ms);
  };
}

function should_stop_portal_reconnect() {
  return portal_manual_disconnect || !auth_checker() || !portal_realtime_portal_id;
}

function schedule_portal_reconnect(delay_ms) {
  const next_delay_ms = get_next_portal_reconnect_delay(delay_ms);
  portal_reconnect_attempts += 1;
  portal_reconnect_timer = setTimeout(() => {
    connect_portal_realtime_socket(portal_realtime_portal_id, {
      delay_ms,
      force: true,
      is_reconnect: true,
    });
  }, next_delay_ms);
}

function handle_portal_socket_close(safe_socket_url, portal_id, delay_ms) {
  portal_realtime_socket = null;
  dispatch_socket_event("portal-realtime:disconnected", {
    socket_url: safe_socket_url,
    portal_id,
    manual: portal_manual_disconnect,
  });

  if (should_stop_portal_reconnect()) {
    portal_manual_disconnect = false;
    portal_reconnect_attempts = 0;
    return;
  }

  schedule_portal_reconnect(delay_ms);
}

function open_portal_socket(normalized_portal_id, token, socket_options) {
  portal_realtime_portal_id = normalized_portal_id;
  portal_realtime_company_id = get_active_organization_id();
  portal_manual_disconnect = false;

  const base_url = get_portal_socket_base_url(normalized_portal_id, portal_realtime_company_id);
  const { socket_url, safe_socket_url } = build_socket_urls(base_url, token);
  portal_realtime_socket = new WebSocket(socket_url);
  portal_realtime_socket.__was_reconnect = socket_options.is_reconnect;
  register_portal_socket_handlers(
    portal_realtime_socket,
    safe_socket_url,
    normalized_portal_id,
    socket_options.delay_ms
  );

  return portal_realtime_socket;
}

export function set_realtime_socket_auth_checker(checker) {
  auth_checker = typeof checker === "function" ? checker : () => true;
}

export function disconnect_realtime_socket() {
  clear_reconnect_timer();
  reconnect_attempts = 0;

  if (!realtime_socket) return;

  manual_disconnect = true;
  realtime_socket.close();
}

export function disconnect_portal_realtime_socket() {
  clear_portal_reconnect_timer();
  portal_reconnect_attempts = 0;
  portal_realtime_portal_id = null;
  portal_realtime_company_id = null;

  if (!portal_realtime_socket) return;

  portal_manual_disconnect = true;
  portal_realtime_socket.close();
}

export function connect_realtime_socket(options = {}) {
  const delay_ms = options.delay_ms ?? reconnect_delay_ms;
  const force = options.force ?? false;
  const is_reconnect = options.is_reconnect ?? false;

  reconnect_delay_ms = delay_ms;

  if (!can_connect()) {
    disconnect_realtime_socket();
    return null;
  }

  if (should_reuse_existing_socket(force, realtime_socket)) {
    return realtime_socket;
  }

  clear_reconnect_timer();
  manual_disconnect = false;

  const token = get_token();


  if (!token) {
    console.warn("[realtime] No token found. WebSocket not opened.");
    return null;
  }

  const base_url = get_socket_url();
  const { socket_url, safe_socket_url } = build_socket_urls(base_url, token);


  realtime_socket = new WebSocket(socket_url);
  realtime_socket.__was_reconnect = is_reconnect;
  register_socket_handlers(realtime_socket, socket_url, safe_socket_url, delay_ms);

  return realtime_socket;
}

export function connect_portal_realtime_socket(portal_id, options = {}) {
  const active_company_id = get_active_organization_id();

  if (!can_open_portal_socket(portal_id)) {
    disconnect_portal_realtime_socket();
    return null;
  }

  const normalized_portal_id = String(portal_id);
  const normalized_company_id = active_company_id;
  const socket_options = get_portal_socket_options(options);

  if (should_reuse_portal_socket(socket_options.force, normalized_portal_id, normalized_company_id)) {
    return portal_realtime_socket;
  }

  close_existing_portal_socket();

  const token = get_token();
  if (!token) return null;

  return open_portal_socket(normalized_portal_id, token, socket_options);
}

export function connect_organisation_realtime_socket(options = {}) {
  const active_company_id = get_active_organization_id();

  if (!active_company_id) {
    return null;
  }

  return connect_portal_realtime_socket(active_company_id, options);
}

export function get_realtime_socket() {
  return realtime_socket;
}
